<template>
  <v-form ref="form" class="form">
    <v-row>
      <v-col class="py-0" cols="12">
        <TreeSelector
          v-model="meter._group.id"
          :loading="resourceLoading"
          :rules="
            resourceLoading
              ? []
              : [rules.required, backEndValidation('group_id')]
          "
          :label="$t('meters.group')"
          :items="groups"
          :options-for-tree-items="{ groupKey: '_ancestors' }"
        ></TreeSelector>
      </v-col>
      <v-col class="py-0" cols="12" sm="6">
        <v-text-field
          v-model="meter.name"
          :label="$t('meters.name')"
          :loading="resourceLoading"
          :rules="
            resourceLoading ? [] : [rules.required, backEndValidation('name')]
          "
          :readonly="readonly"
        ></v-text-field>
      </v-col>
      <v-col class="py-0" cols="12" sm="6">
        <v-text-field
          v-model="meter.device_type"
          :label="$t('meters.device-type')"
          :loading="resourceLoading"
          :rules="
            resourceLoading
              ? []
              : [rules.required, backEndValidation('device_type')]
          "
          :readonly="readonly"
        ></v-text-field>
      </v-col>
      <v-col class="py-0" cols="12" sm="6">
        <v-text-field
          v-model="meter.mounted_location"
          :label="$t('meters.mounted-location')"
          :loading="resourceLoading"
          :rules="
            resourceLoading ? [] : [backEndValidation('mounted_location')]
          "
          :readonly="readonly"
        ></v-text-field>
      </v-col>
      <v-col class="py-0" cols="12" sm="6">
        <v-text-field
          v-model="meter.serial_number"
          :label="$t('meters.serial-number')"
          :loading="resourceLoading"
          :rules="resourceLoading ? [] : [backEndValidation('serial_number')]"
          :readonly="readonly"
        ></v-text-field>
      </v-col>
      <v-col class="py-0" cols="12" sm="6">
        <v-text-field
          v-model="meter.description"
          :label="$t('meters.description')"
          :loading="resourceLoading"
          :rules="resourceLoading ? [] : [backEndValidation('description')]"
          :readonly="readonly"
          maxlength="190"
        ></v-text-field>
      </v-col>
      <v-col class="py-0" cols="12" sm="6">
        <v-autocomplete
          v-model="meter._medium.id"
          :items="mediums"
          item-text="name"
          item-value="id"
          :label="$t('meters.medium')"
          :loading="resourceLoading"
          :rules="
            resourceLoading
              ? []
              : [rules.required, backEndValidation('medium_id')]
          "
          :readonly="readonly"
        ></v-autocomplete>
      </v-col>
      <v-col class="py-0" cols="12">
        <v-textarea
          v-model="meter.notices"
          :label="$t('meters.notices')"
          :loading="resourceLoading"
          :rules="resourceLoading ? [] : [backEndValidation('notices')]"
          :readonly="readonly"
        ></v-textarea>
      </v-col>
    </v-row>
    <template v-if="!hideButtons">
      <ButtonWithMenu
        :loading="loading"
        :disabled="readonly"
        :menu-hide="button_title !== 'create'"
        :general-button="{
          icon: $props.button_icon,
          text: `buttons.${$props.button_title}`,
          eventFunction: save
        }"
        :menu-items="[
          {
            icon: $props.button_icon,
            text: `buttons.create-and-new`,
            eventFunction: saveAndNew
          },
          {
            icon: $props.button_icon,
            text: `buttons.create-and-clone`,
            eventFunction: saveAndClone
          }
        ]"
      >
      </ButtonWithMenu>
      <template v-if="button_title !== 'create'">
        <v-btn
          class="ml-4"
          color="primary"
          :to="{
            name: 'new-meter',
            query: {
              meter: meter.id
            }
          }"
          :disabled="readonly"
          :loading="loading"
        >
          <v-icon left>mdi-plus</v-icon>
          {{ $t(`buttons.clone`) }}
        </v-btn>
      </template>
    </template>
  </v-form>
</template>

<script>
import { rules } from "../../../mixins/rules";
import { projectPermissions } from "../../../mixins/projects/projectPermissions";
import ButtonWithMenu from "../../../common/buttons/ButtonWithMenu";
import TreeSelector from "../../../common/inputs/TreeSelector";
export default {
  name: "FormMeter",
  mixins: [rules, projectPermissions],
  components: { ButtonWithMenu, TreeSelector },
  data: () => ({
    loading: false
  }),
  props: {
    dispatch: String,
    button_title: String,
    button_icon: String,
    meter: {
      default: function() {
        return {
          _medium: { id: null },
          _group: { id: null }
        };
      }
    },
    resourceLoading: {
      type: Boolean,
      default: false
    },
    hideButtons: {
      type: Boolean,
      default: false
    },
    permission: {
      type: String,
      default: ""
    }
  },
  computed: {
    mediums() {
      return this.$store.getters["mediumStore/getList"];
    },
    groups() {
      return this.$store.getters["projectMeteringStore/groupStore/getList"];
    },
    readonly: {
      get() {
        return !this.hasCurrentProjectPermissions(this.permission);
      }
    }
  },
  methods: {
    save(callback = this.defaultCallbackForSave) {
      this.clearBackEndErrors();
      if (this.$refs.form.validate()) {
        this.loading = true;
        let item = {
          ...this.meter,
          project_id: this.$route.params.project_id
        };
        this.$store
          .dispatch(this.$props.dispatch, {
            item,
            errorHandler: this.errorHandler.bind(this, () => {
              this.$refs.form.validate();
            }),
            onSuccess: item => {
              if (item !== "") {
                callback(item);
              }
            }
          })
          .finally(() => (this.loading = false));
      }
    },
    saveAndClone() {
      let callback = i => {
        let { id, ...item } = i;
        this.$store.commit("projectMeteringStore/meterStore/set", { item });
        this.$router.push({
          name: "new-meter",
          query: {
            meter: id
          }
        });
        this.$store.dispatch(
          "notificationStore/createSuccessNotificationForApiRequest",
          {
            text: this.$t(`message.success-create`)
          }
        );
      };
      this.save(callback);
    },
    saveAndNew() {
      let callback = () => {
        this.$route.query.meter && this.$router.push({ name: "new-meter" });
        for (let member in this.meter) {
          let value = null;
          if (member === "_medium" || member === "_group") {
            value = { id: null };
          }
          this.$set(this.meter, member, value);
          this.$refs.form.resetValidation();
        }
      };
      this.save(callback);
    },
    defaultCallbackForSave(item) {
      this.$store.commit("projectMeteringStore/meterStore/set", {
        item
      });
      this.$router.push({
        name: "meter",
        params: {
          id: item.id,
          project_id: this.$route.params.project_id
        }
      });
    }
  },
  mounted() {
    this.$store.dispatch("mediumStore/getList", {
      params: { limit: -1 }
    });
    this.$store.dispatch("projectMeteringStore/groupStore/getList", {
      params: {
        limit: -1,
        project_id: this.$route.params.project_id
      }
    });
  }
};
</script>
